/**
 * Hook for fetching derived assets for a single parent asset.
 *
 * Use for detailed views (timeline player).
 * For list views, use the inline derivedAssets from useAssets instead.
 *
 * Primary invalidation is via WebSocket events (useDerivedAssetEvents.ts).
 * Polling at 30s is a fallback only for WS reconnection scenarios.
 */

import { useQuery } from "@tanstack/react-query";
import { listDerivedAssets } from "@/services/api/sdk.gen";
import { DerivedAsset } from "@/types/asset.types";
import { useMemo, useCallback } from "react";

export interface DerivedAssetProgress {
  /** Number of chunks uploaded so far */
  uploaded: number;
  /** Total expected chunks (null if unknown during processing) */
  total: number | null;
  /** Completion percentage (null if total is unknown) */
  percent: number | null;
}

export function useDerivedAssets(assetId: string | undefined) {
  const query = useQuery({
    queryKey: ["derived-assets", assetId],
    queryFn: async () => {
      if (!assetId) return [];
      const result = await listDerivedAssets({
        path: { assetId },
        throwOnError: true,
      });
      return (result?.data?.data as DerivedAsset[]) || [];
    },
    enabled: !!assetId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Fallback polling when derived assets are processing/pending.
    // Primary invalidation is via WebSocket events (useDerivedAssetEvents.ts).
    // 30s polling is a safety net for WS reconnection scenarios only.
    refetchInterval: (query) => {
      const data = query.state.data as DerivedAsset[] | undefined;
      if (!data) return false;
      const hasActive = data.some(
        (da) => da.status === "processing" || da.status === "pending",
      );
      return hasActive ? 30000 : false;
    },
  });

  const derivedAssets = useMemo(() => query.data || [], [query.data]);

  // Helper selectors
  const getByRuleKey = (ruleKey: string) =>
    derivedAssets.filter(
      (da) => da.ruleKey === ruleKey && da.status === "complete",
    );

  const getChunks = (resolution?: string) => {
    const chunks = getByRuleKey("video_chunks_v1");
    if (resolution)
      return chunks.filter((da) => da.params.resolution === resolution);
    return chunks;
  };

  const getThumbnail = () => getByRuleKey("thumbnail_v1")[0] ?? null;
  const getWaveform = () => getByRuleKey("waveform_v1")[0] ?? null;
  const getFilmstrips = () => getByRuleKey("filmstrip_v1");

  // Per-derived-asset upload progress.
  // During processing, fileCount reflects chunks uploaded so far (incremental DB updates).
  const getProgress = useCallback(
    (derivedAssetId: string): DerivedAssetProgress | null => {
      const da = derivedAssets.find((d) => d.id === derivedAssetId);
      if (!da) return null;
      if (da.status === "complete") {
        return {
          uploaded: da.fileCount ?? 0,
          total: da.fileCount ?? 0,
          percent: 100,
        };
      }
      if (
        da.status === "processing" &&
        da.fileCount != null &&
        da.fileCount > 0
      ) {
        return { uploaded: da.fileCount, total: null, percent: null };
      }
      return { uploaded: 0, total: null, percent: null };
    },
    [derivedAssets],
  );

  // Aggregate processing status
  const processingStatus = useMemo(() => {
    if (derivedAssets.length === 0) return "none";
    if (derivedAssets.some((da) => da.status === "processing"))
      return "processing";
    if (derivedAssets.some((da) => da.status === "pending")) return "pending";
    if (derivedAssets.some((da) => da.status === "failed"))
      return "partial_failure";
    if (derivedAssets.every((da) => da.status === "complete"))
      return "complete";
    return "unknown";
  }, [derivedAssets]);

  return {
    derivedAssets,
    isLoading: query.isLoading,
    error: query.error,
    getChunks,
    getThumbnail,
    getWaveform,
    getFilmstrips,
    getProgress,
    processingStatus,
    refetch: query.refetch,
  };
}
